import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  LinearProgress,
} from "@mui/material";
import { Warning, Timer } from "@mui/icons-material";

interface ModalAvisoLogoutProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  tempoRestante: number;
  tempoTotal: number;
}

const ModalAvisoLogout: React.FC<ModalAvisoLogoutProps> = ({
  open,
  onClose,
  onConfirm,
  tempoRestante,
  tempoTotal,
}) => {
  const [tempoAtual, setTempoAtual] = useState(tempoRestante);

  useEffect(() => {
    if (open && tempoAtual > 0) {
      const timer = setInterval(() => {
        setTempoAtual((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            onConfirm(); // Executa logout automático
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [open, tempoAtual, onConfirm]);

  useEffect(() => {
    if (open) {
      setTempoAtual(tempoRestante);
    }
  }, [open, tempoRestante]);

  // Garante que o modal não pode ser fechado e executa logout automático
  useEffect(() => {
    if (open) {
      // Timer de segurança para garantir logout automático
      const safetyTimer = setTimeout(
        () => {
          if (open) {
            onConfirm();
          }
        },
        (tempoTotal + 2) * 1000,
      ); // +2 segundos para garantir

      return () => clearTimeout(safetyTimer);
    }
  }, [open, tempoTotal, onConfirm]);

  const progresso = ((tempoTotal - tempoAtual) / tempoTotal) * 100;

  return (
    <Dialog
      open={open}
      onClose={() => {}} // Desabilita fechar clicando fora
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown // Desabilita fechar com ESC
      PaperProps={{
        style: {
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center", // Centraliza o título
          backgroundColor: "#28a745",
          color: "white",
          borderBottom: "1px solid #1e7e34",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Warning sx={{ color: "white" }} />
          <Typography variant="h6" component="div">
            Sessão Expirada
          </Typography>
        </Box>
        {/* Removido o botão de fechar (X) */}
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
            Sua sessão foi encerrada por um novo login em outro dispositivo.
          </Typography>
        </Alert>

        <Typography variant="body1" sx={{ mb: 2 }}>
          Você será desconectado automaticamente em:
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
            gap: 1,
          }}
        >
          <Timer sx={{ color: "#28a745" }} />
          <Typography
            variant="h4"
            sx={{
              color: tempoAtual <= 5 ? "#dc3545" : "#28a745",
              fontWeight: "bold",
              fontFamily: "monospace",
            }}
          >
            {tempoAtual}s
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progresso}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: "#d4edda",
            "& .MuiLinearProgress-bar": {
              backgroundColor: tempoAtual <= 5 ? "#dc3545" : "#28a745",
              borderRadius: 4,
            },
          }}
        />

        <Typography
          variant="body2"
          sx={{
            mt: 2,
            textAlign: "center",
            color: "#666",
            fontStyle: "italic",
          }}
        >
          Este aviso não pode ser fechado. O logout será executado
          automaticamente.
        </Typography>

        <Typography
          variant="body2"
          sx={{
            mt: 1,
            textAlign: "center",
            color: "#28a745",
            fontWeight: "bold",
          }}
        >
          ⚠️ IMPORTANTE: Não feche esta janela!
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1, justifyContent: "center" }}>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="success"
          sx={{
            minWidth: "120px",
            backgroundColor: "#28a745",
            "&:hover": {
              backgroundColor: "#218838",
            },
          }}
        >
          Sair Agora
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalAvisoLogout;
